import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

const FinancialLiteracy = () => {
  const navigate = useNavigate()
  const { user, session } = useAuth()
  const [accessStatus, setAccessStatus] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (user && session?.access_token) {
      fetchAccessStatus()
    } else {
      setLoading(false)
    }
  }, [user, session])

  const fetchAccessStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/access-status`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setAccessStatus(data)
      }
    } catch (error) {
      console.error('Error fetching access status:', error)
    } finally {
      setLoading(false)
    }
  }

  const hasActiveSchoolAccess = accessStatus?.access?.school_financial_literacy === true
  const schoolInfo = accessStatus?.school_info

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-green-500 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-white/20 rounded-full text-sm font-semibold mb-4">
              Pre žiakov 4. – 9. ročníka
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Finančná gramotnosť pre školy
            </h1>
            <p className="text-xl text-green-50 mb-8 leading-relaxed">
              Praktický vzdelávací program, ktorý pripravuje žiakov na skutočné finančné rozhodnutia prostredníctvom interaktívnych scenárov a hier.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          
          {/* What's Included Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Čo program obsahuje?</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              {/* Feature 1 */}
              <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Interaktívne scenáre</h3>
                <p className="text-gray-600 leading-relaxed">
                  Realistické finančné situácie, ktoré žiaci môžu stretnúť v reálnom živote – od sporenia po plánovanie výdavkov.
                </p>
              </div>

              {/* Feature 2 */}
              <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Rozhodovacie úlohy</h3>
                <p className="text-gray-600 leading-relaxed">
                  Žiaci sa učia zvažovať rôzne možnosti, porovnávať riziká a vyberať správne finančné rozhodnutia.
                </p>
              </div>

              {/* Feature 3 */}
              <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Praktické aktivity</h3>
                <p className="text-gray-600 leading-relaxed">
                  Mini simulácie, príbehové úlohy a skupinové aktivity, ktoré robia výučbu finančnej gramotnosti zážitkovou.
                </p>
              </div>
            </div>
          </div>

          {/* How it works in classroom */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Ako to funguje na hodine</h2>
            <div className="max-w-3xl mx-auto">
              <div className="flex flex-col md:flex-row items-center gap-6 mb-8">
                <div className="flex-1 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-3xl">💡</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">1. Rozhodnutie</h3>
                  <p className="text-sm text-gray-600">
                    Žiak sa rozhoduje v reálnej finančnej situácii
                  </p>
                </div>
                
                <div className="text-3xl text-gray-300 hidden md:block">→</div>
                
                <div className="flex-1 text-center">
                  <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-3xl">📊</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">2. Dôsledok</h3>
                  <p className="text-sm text-gray-600">
                    Vidí výsledky svojho rozhodnutia a jeho následky
                  </p>
                </div>
                
                <div className="text-3xl text-gray-300 hidden md:block">→</div>
                
                <div className="flex-1 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-3xl">💬</span>
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">3. Diskusia</h3>
                  <p className="text-sm text-gray-600">
                    Trieda diskutuje o rôznych riešeniach a poučeniach
                  </p>
                </div>
              </div>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg">
                <p className="text-gray-700 text-sm">
                  <strong>Tip:</strong> Každý scenár trvá 10–15 minút a je navrhnutý tak, aby inicioval diskusiu o zodpovednom nakladaní s peniazmi.
                </p>
              </div>
            </div>
          </div>

          {/* For teachers section */}
          <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-8 md:p-12 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Pre učiteľa</h2>
            <div className="max-w-2xl mx-auto space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mt-1">
                  <span className="text-green-600 font-bold text-lg">✓</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Použiteľné okamžite na hodine
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Žiadna príprava nie je potrebná – otvoríte scenár a môžete začať
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mt-1">
                  <span className="text-green-600 font-bold text-lg">✓</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Žiadna príprava
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Všetky scenáre sú pripravené s inštrukciami a návodnými otázkami pre diskusiu
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mt-1">
                  <span className="text-green-600 font-bold text-lg">✓</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Vhodné na diskusiu
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Každý scenár má viacero možných riešení – ideálne pre skupinovú diskusiu a kritické myslenie
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-8 md:p-12 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Prečo zvoliť náš program?</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mt-1">
                  <span className="text-green-600 font-bold text-lg">✓</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Pripravený na použitie
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Kompletný program s podrobnými učebnými materiálmi a inštruktážami pre učiteľov
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mt-1">
                  <span className="text-green-600 font-bold text-lg">✓</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Prístup pre celú školu
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Neomedzený počet žiakov a učiteľov – jeden ročný prístup pre celú inštitúciu
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mt-1">
                  <span className="text-green-600 font-bold text-lg">✓</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Sledovanie pokroku
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Online platforma so štatistikami a prehľadom výsledkov žiakov
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Demo Scenario Section */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-16">
            <div className="text-center mb-8">
              <div className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">
                🎮 Vyskúšaj ukážku
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Interaktívne scenáre</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Zažite, ako fungujú naše scenáre! Vyberte si ročník a vyskúšajte si ukážkové scenáre zadarmo.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 mb-6">
              <div className="text-center mb-4">
                <div className="text-5xl mb-3">📚</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Scenáre pre 1. – 9. ročník</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Každý ročník obsahuje scenáre prispôsobené vekovej úrovni žiakov
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="bg-white rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">🏫</span>
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">Školský bufet</div>
                      <div className="text-xs text-gray-600">10 krokov • 4. ročník</div>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">📱</span>
                    <div>
                      <div className="font-semibold text-gray-900 text-sm">Mobil a kredity</div>
                      <div className="text-xs text-gray-600">10 krokov • 4. ročník</div>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={() => navigate('/scenarios')}
                className="w-full px-6 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 transition shadow-md hover:shadow-lg"
              >
                Vybrať ročník a začať →
              </button>
            </div>

            <p className="text-sm text-gray-500 text-center">
              Žiadne správne či nesprávne odpovede – iba rozhodnutia a ich dôsledky
            </p>
          </div>

          {/* Pricing and CTA */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
            
            {loading ? (
              // Loading state
              <div className="py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Načítavam stav prístupu...</p>
              </div>
            ) : hasActiveSchoolAccess && schoolInfo ? (
              // Active school package state
              <div>
                <div className="mb-6">
                  <div className="text-6xl mb-4">✅</div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">Balík je aktívny</h2>
                  <p className="text-gray-600">Vaša škola má prístup k programu finančnej gramotnosti</p>
                </div>

                <div className="max-w-2xl mx-auto mb-8">
                  <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between border-b border-green-200 pb-3">
                        <span className="text-sm font-semibold text-gray-700">Škola:</span>
                        <span className="text-sm font-bold text-gray-900">{schoolInfo.school_name}</span>
                      </div>
                      <div className="flex items-center justify-between border-b border-green-200 pb-3">
                        <span className="text-sm font-semibold text-gray-700">Platnosť do:</span>
                        <span className="text-sm font-bold text-green-600">
                          {new Date(schoolInfo.access_until).toLocaleDateString('sk-SK', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-gray-700">Status:</span>
                        <span className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                          Aktívny
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => navigate('/scenarios')}
                  className="px-10 py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition shadow-lg hover:shadow-xl"
                >
                  Prejsť na scenáre →
                </button>
                <p className="text-sm text-gray-500 mt-4">
                  Zobraziť všetky dostupné scenáre pre vašu školu
                </p>
              </div>
            ) : (
              // No active access - show purchase flow
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Ročný prístup pre školy: 250 €</h2>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-green-600">250 €</span>
                  <span className="text-gray-600 text-lg ml-2">/ rok</span>
                </div>
                
                {/* Value explanation */}
                <div className="max-w-2xl mx-auto mb-8">
                  <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-xl p-6 text-left">
                    <h3 className="font-bold text-gray-900 mb-4 text-center">Čo je zahrnuté v cene?</h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <span className="text-green-600 font-bold mt-1">✓</span>
                        <p className="text-gray-700 text-sm">
                          <strong>Prístup ku všetkým scenárom</strong> – interaktívne cvičenia pre žiakov 4.–9. ročníka
                        </p>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-green-600 font-bold mt-1">✓</span>
                        <p className="text-gray-700 text-sm">
                          <strong>Pripravené aktivity do triedy</strong> – bez potreby prípravy, použiteľné okamžite
                        </p>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-green-600 font-bold mt-1">✓</span>
                        <p className="text-gray-700 text-sm">
                          <strong>Neomedzený počet žiakov</strong> – celá škola má prístup k programu
                        </p>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-green-600 font-bold mt-1">✓</span>
                        <p className="text-gray-700 text-sm">
                          <strong>Nové scenáre pridávané priebežne</strong> – váš prístup sa neustále rozširuje
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => navigate('/school-purchase')}
                  className="px-10 py-4 bg-green-600 text-white rounded-xl font-bold text-lg hover:bg-green-700 transition shadow-lg hover:shadow-xl"
                >
                  Mám záujem o program
                </button>
                <p className="text-sm text-gray-500 mt-4">
                  Vyplňte údaje školy a pokračujte k objednávke
                </p>
              </div>
            )}
          </div>

        </div>
      </div>

    </div>
  )
}

export default FinancialLiteracy
